import React from 'react';
import Joi from 'joi';
import PropTypes from 'prop-types';

import _ from 'lodash';
import dayjs from 'dayjs';

import { CoverDataItem, CustomLPTReportItem } from '../../../utils/models';
import { getCustomLPTColumns, getLPTModalData } from '../../../utils/data';
import { SURFACE_TEMPERATURE } from '../custom-mpi/mpiReportForm';
import { MATERIAL_OPTIONS } from '../../../utils/constants';

import AutTable from '../../blocks/aut-grid-table/index';
import EditLPTReportItemForm from './editLPTReportItemForm';
import Form from '../../common/form';
import LPTReportItemForm from './lptReportItemForm';

import toastService from '../../../services/toastService';

class LPTReportForm extends Form {
	state = {
		data: {
			developingTime: '',
			dateOfInspection: new Date(),
			calibrationDueDate: new Date(),
			penetrantExpiryDate: new Date(),
			penetrantRemoverExpiryDate: new Date(),
			developerExpiryDate: new Date(),
			penetrantBatchNumber: '',
			penetrantRemoverBatchNumber: '',
			developerBatchNumber: '',
			modal: '',
			lightType: '',
			columnUsed: '',
			blackInkUsed: '',
			aerosolsType: '',
			serialNumber: '',
			surfaceCondition: '',
			surfaceTemperature: '',
			developer: '',
			dwellTime: '',
			penetrant: '',
			penetrantRemover: '',
			material: '',
			stageOfTest: '',
			equipment: 'Penetrant Testing Kit & Rags',
			light: '',
			technique: '',
			data: [],
		},
		errors: {},
		loading: false,
		progress: 0,
		reportItemToEdit: null,
	};

	schema = {
		surfaceCondition: Joi.string().min(3).required().label('Surface Condition'),
		dateOfInspection: Joi.date().less('now').required().label('Date of Inspection'),
		calibrationDueDate: Joi.date().greater('now').required().label('Light Meter Calibration Due Date'),
		stageOfTest: Joi.string().min(3).required().label('Stage of Test'),
		equipment: Joi.string().min(3).required().label('Equipment'),
		light: Joi.string().min(3).required().label('Light Intensity'),
		modal: Joi.object({
			label: Joi.string().required(),
			value: Joi.string().required(),
			serialNumber: Joi.number().optional(),
			__isNew__: Joi.boolean().optional(),
		}).required().label('Lux Meter Model'),
		serialNumber: Joi.string().min(2).required().label('Lux Meter Serial Number'),
		dwellTime: Joi.string().min(3).required().label('Dwell Time'),
		blackInkUsed: Joi.string().min(3).required().label('Black Ink Used'),
		columnUsed: Joi.string().min(3).required().label('Column Used'),
		developingTime: Joi.string().min(3).required().label('Developing Time'),
		penetrant: Joi.string().min(3).required().label('Penentrant'),
		aerosolsType: Joi.string().min(3).required().label('Aerosols Type'),
		lightType: Joi.string().min(3).required().label('Light Type'),
		penetrantRemover: Joi.string().min(3).required().label('Penetrant Remover'),
		penetrantBatchNumber: Joi.string().min(3).required().label('Penetrant Batch Number'),
		penetrantExpiryDate: Joi.date().greater('now').required().label('Penetrant Expiration Date'),
		penetrantRemoverBatchNumber: Joi.string().min(3).required().label('Penetrant Remover Batch Number'),
		penetrantRemoverExpiryDate: Joi.date().greater('now').required().label('Penetrant Remover Expiration Date'),
		developerBatchNumber: Joi.string().min(3).required().label('Developer Batch Number'),
		developerExpiryDate: Joi.date().greater('now').required().label('Developer Expiration Date'),
		developer: Joi.string().min(3).required().label('Developer'),
		surfaceTemperature: Joi.string().min(3).required().label('Surface Temperature'),
		technique: Joi.string().min(3).required().label('Technique'),
		material: Joi.string().required().label('Material'),
		data: Joi.array()
			.min(1)
			.items(
				Joi.object({
					joint: Joi.string().min(3).required().label('Joint/Job Number'),
					observation: Joi.string().min(10).required().label('Observation'),
					result: Joi.string().min(3).required().label('Result'),
					images: Joi.array().min(0).required().label('Image'),
				})
			)
			.required()
			.label('Inspection Items'),
	};

	static propTypes = {
		photoNumbers: PropTypes.array,
		onAdd: PropTypes.func,
	};

	componentDidMount() {
		if (this.props.initialLPTData) {
			const initialData = { ...this.props.initialLPTData } as typeof this.state.data;

			if (_.isString(initialData.modal)) {
				const optionMatch = getLPTModalData().find((option) => option.value === initialData.modal);
				
				if (optionMatch) initialData.modal = optionMatch as any;
				else initialData.modal = { label: initialData.modal, value: initialData.modal } as any;
			}

			this.setState({ data: _.omit(initialData, ['refStd', 'acceptanceStd', 'summary']) });
		}
	}

	componentDidUpdate(prevProps: Readonly<any>, prevState: Readonly<{ data: any; errors: any; progress: number; loading: boolean; touched?: boolean; }>, snapshot?: any): void {
		const data = { ...this.state.data };
		const modal = data.modal as unknown as CoverDataItem;

		if (modal && prevState.data.modal?.value !== modal.value) {
			const optionMatch = getLPTModalData().find((option) => option.value === modal.value);
			if (optionMatch) data.serialNumber = optionMatch.serialNumber.toString();
		}

		if (prevState.data.technique !== this.state.data.technique) {
			const newTechnique = this.state.data.technique.toLowerCase();
			data.lightType = ['visible', 'non-fluorescence'].includes(newTechnique) ? 'Visible' : 'Fluorescence';
		}

		if (_.isEqual(data, this.state.data)) {
			return;
		}
		
		this.setState({ data });
	}

	onAddItem = (newItem: CustomLPTReportItem) => {
		const reportItems: CustomLPTReportItem[] = this.state.data.data;

		const match: any = reportItems.find(({ joint }) => joint === newItem.joint);
		if (match) toastService.error(`Entry for ${match.joint} already exists!`);

		this.setState({ data: { ...this.state.data, data: [...this.state.data.data, newItem] }});
	};

	onDeleteItem = (item: CustomLPTReportItem): void => {
		const reportItems: CustomLPTReportItem[] = this.state.data.data;
		const newMPTItems = reportItems.filter(({ joint }) => joint !== item.joint);

		this.setState({ data: { ...this.state.data, data: newMPTItems } });
	};

	onEditItem = (item: CustomLPTReportItem): void => {
		const reportItems: CustomLPTReportItem[] = this.state.data.data;
		const originalReportItem  = this.state.reportItemToEdit as unknown as CustomLPTReportItem;
		const newMPTItems = reportItems.map((reportItem) => (reportItem.joint === originalReportItem!.joint) ? item : reportItem);

		this.setState({
			data: { ...this.state.data, data: newMPTItems },
			reportItemToEdit: null,
		} as any);
	};

	onSelectEditItem = (item: CustomLPTReportItem): void => {
		this.setState({ reportItemToEdit: item } as any);
	};

	onCancelEdit = () => {
		this.setState({ reportItemToEdit: null } as any);
	};

	getApiData = () => {
		const modal = this.state.data.modal as unknown as CoverDataItem;
		return { ...this.state.data, modal: modal.value };
	}

	doSubmit = (): void => {
		this.props.onAdd(this.getApiData());
		toastService.success('LPT Data Saved Successfully!');
	};

	render() {
		return (
			<React.Fragment>
				{this.state.reportItemToEdit ? (
					<EditLPTReportItemForm
						initialData={this.state.reportItemToEdit}
						onAdd={this.onAddItem}
						photoNumbers={this.props.photoNumbers}
						onEdit={this.onEditItem}
						onCancelEdit={this.onCancelEdit}
					/>
				) : (
					<LPTReportItemForm
						onAdd={this.onAddItem}
						photoNumbers={this.props.photoNumbers}
					/>
				)}

				<div className="divider my-4"></div>

				<AutTable
					tableColumns={getCustomLPTColumns()}
					data={this.state.data.data}
					onDeleteItem={this.onDeleteItem}
					onEditItem={this.onSelectEditItem}
				/>

				<form autoComplete="off" onSubmit={this.handleSubmit}>
					<div className="row">
						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'surfaceCondition',
								label: 'Surface Condition',
								placeholder: 'Enter Surface Condition',
							})}
						</div>

						<div className="col">
							{this.renderDatePicker({
								name: 'dateOfInspection',
								label: 'Date of Inspection',
								placeholder: 'Select Date of Inspection',
								maxDate: new Date()
							})}
						</div>

						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'stageOfTest',
								label: 'Stage of Test',
								placeholder: 'Enter Stage of Test',
							})}
						</div>

						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'columnUsed',
								label: 'Column Used',
								placeholder: 'Enter Column Used',
							})}
						</div>

						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'blackInkUsed',
								label: 'Black Ink Used',
								placeholder: 'Enter Black Ink Used',
							})}
						</div>

						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'equipment',
								label: 'Equipment',
								placeholder: 'Enter Equipment',
							})}
						</div>

						<div className="col">
							{this.renderSelect({
								options: MATERIAL_OPTIONS,
								name: 'material',
								label: 'Material',
								placeholder: 'Enter Material',
							})}
						</div>
						
						<div className="col">
							{this.renderSelect({
								options: LIGHT_TYPES,
								name: 'lightType',
								label: 'Light Type',
								placeholder: 'Enter Light Type',
							})}
						</div>
					</div>

					<div className="my-4 divider"></div>

					<div className="row">
						<div className="col">
							{this.renderSelect({
								options: AEROSOL_TYPES,
								name: 'aerosolsType',
								label: 'Aerosols Type',
								placeholder: 'Select an Option',
							})}
						</div>

						<div className="col">
							{this.renderSelect({
								options: TIMES,
								name: 'dwellTime',
								label: 'Dwell Time',
								placeholder: 'e.g. 10 minutes',
							})}
						</div>

						<div className="col">
							{this.renderSelect({
								options: TIMES,
								name: 'developingTime',
								label: 'Developing Time',
								placeholder: 'e.g. 10 minutes',
							})}
						</div>
					</div>

					<div className="my-4 divider"></div>

					<div className="row">
						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'penetrant',
								label: 'Penetrant',
								placeholder: 'Enter Penetrant',
							})}
						</div>

						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'penetrantRemover',
								label: 'Penetrant Remover',
								placeholder: 'Enter Penetrant Remover',
							})}
						</div>

						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'developer',
								label: 'Developer',
								placeholder: 'Enter Developer Name',
							})}
						</div>

						<div className="col">
							{this.renderSelect({
								options: SURFACE_TEMPERATURE,
								name: 'surfaceTemperature',
								label: 'Surface Temperature',
								placeholder: 'Enter Surface Temperature',
							})}
						</div>

						<div className="col">
							{this.renderSelect({
								options: TECHNIQUES,
								name: 'technique',
								label: 'Technique',
								placeholder: 'Enter Technique',
							})}
						</div>
					</div>

					<div className="my-4 divider"></div>
					
					<div className="row">
						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'penetrantBatchNumber',
								label: 'Penetrant Batch Number',
								placeholder: 'Enter Penetrant Batch Number',
							})}
						</div>

						<div className="col">
							{this.renderDatePicker({
								name: 'penetrantExpiryDate',
								label: 'Penetrant Expiry Date',
								placeholderText: 'Select Penetrant Expiry Date',
								minDate: dayjs(this.state.data.dateOfInspection).toDate()
							})}
						</div>
						
						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'penetrantRemoverBatchNumber',
								label: 'Penetrant Remover Batch Number',
								placeholder: 'Enter Penetrant Remover Batch Number',
							})}
						</div>

						<div className="col">
							{this.renderDatePicker({
								name: 'penetrantRemoverExpiryDate',
								label: 'Penetrant Remover Expiry Date',
								placeholderText: 'Select Penetrant Remover Expiry Date',
								minDate: dayjs(this.state.data.dateOfInspection).toDate()
							})}
						</div>
					</div>

					<div className="my-4 divider"></div>

					<div className="row">
						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'developerBatchNumber',
								label: 'Developer Batch Number',
								placeholder: 'Enter Developer Batch Number',
							})}
						</div>

						<div className="col">
							{this.renderDatePicker({
								name: 'developerExpiryDate',
								label: 'Developer Expiry Date',
								placeholderText: 'Select Developer Expiry Date',
								minDate: dayjs(this.state.data.dateOfInspection).toDate()
							})}
						</div>

						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'light',
								label: 'Light Intensity (LUX)',
								placeholder: 'Enter Light Intensity Value',
							})}
						</div>

						<div className="col">
							{this.renderDatePicker({
								name: 'calibrationDueDate',
								label: 'Light Meter Calibration Due Date',
								placeholderText: 'Select Light Meter Calibration Due Date',
								minDate: new Date(),
							})}
						</div>
					</div>

					<div className="my-4 divider"></div>

					<div className="row">
						<div className="col">
							{this.renderCreatableMultiselect({
								closeMenuOnSelect: true,
								options: getLPTModalData(),
								name: 'modal',
								placeholder: 'Enter Lux Meter Model',
								label: 'Lux Meter Model',
								isMulti: false,
							})}
						</div>

						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'serialNumber',
								label: 'Lux Meter Serial Number',
								placeholder: 'Enter Lux Meter Serial Number Value',
							})}
						</div>
					</div>

					<div className="my-4 divider"></div>

					{this.renderButton('Save LPT Data')}
				</form>
			</React.Fragment>
		);
	}
}

const AEROSOL_TYPES = [
	{
		id: "Water Washable",
		name: "Water Washable",
	},
	{
		id: "Solvent Removable",
		name: "Solvent Removable",
	},
];

const TIMES = [
	{
		id: '5 min',
		name: '5 min',
	},
	{
		id: '10 min',
		name: '10 min',
	},
	{
		id: '15 min',
		name: '15 min',
	},
	{
		id: '30 min',
		name: '30 min',
	},
	{
		id: '45 min',
		name: '45 min',
	},
	{
		id: '60 min',
		name: '60 min',
	},
	{
		id: '90 min',
		name: '90 min',
	},
	{
		id: '120 min',
		name: '120 min',
	},
];

const TECHNIQUES = [
	{
		id: 'Visible',
		name: 'Visible',
	},
	{
		id: 'Fluorescence',
		name: 'Fluorescence',
	},
	{
		id: 'Non-Fluorescence',
		name: 'Non-Fluorescence',
	},
];

const LIGHT_TYPES = [
	{
		id: 'Visible',
		name: 'Visible',
	},
	{
		id: 'Fluorescence',
		name: 'Fluorescence',
	},
];

export default LPTReportForm;
